<template>
  <div>
    <text-box :label="title" icon="chart-bar" :hideTitle="hideTitle" :style="scrollableHorizontal ? 'height: 412px;' : ''">
      <template v-slot:info>
        <percent-chip v-if="precision" :value="precision" :best="100" icon="mdi-bullseye-arrow"/>
        <slot name="info"></slot>
      </template>
      <v-container fluid>
        <v-row>
          <v-col cols="12">
            <div v-if="scrollableHorizontal" class="echart-wrap">
              <v-chart :option="options" :init-options="{renderer: 'canvas'}" autoresize :style="`width: 100%; height: ${chartHeight}px;`"/>
            </div>
            <v-chart v-else :option="options" :init-options="{renderer: 'canvas'}" autoresize style="height: 340px; width: 100%;"/>
          </v-col>
        </v-row>
      </v-container>
    </text-box>
  </div>
</template>

<script>
import ECharts from 'vue-echarts'
import 'echarts/lib/chart/bar'
import 'echarts/lib/component/tooltip'
import { graphic } from 'echarts/core'
// import 'zrender/lib/svg/svg'
import 'zrender/lib/canvas/canvas'
import numbro from 'numbro'
import moment from 'moment'
import TextBox from '@/components/TextBox.vue'
import PercentChip from '@/components/PercentChip.vue'

export default {
  data () {
    return {}
  },
  computed: {
    scrollableHorizontal () {
      return this.horizontal && this.data.x.length > 13
    },
    chartHeight () {
      return this.data.x.length * 25
    },
    yAxisNameGap () {
      if (!this.fullPrecisionLabels || this.horizontal) return 50
      if (!this.data || !this.data.y || !this.data.y.length) return 50
      const maxLen = Math.max(
        ...this.data.y.map(v => (v == null ? 0 : String(v).length))
      )
      return Math.max(50, maxLen * 7 + 15)
    },
    seriesData () {
      if (!this.colors) return this.data.y
      var self = this
      var horiz = this.horizontal
      return this.data.y.map(function (value, index) {
        var name = self.data.x[index]
        var color = self.colors[name]
        if (color) {
          return {
            value: value,
            itemStyle: {
              color: horiz
                ? new graphic.LinearGradient(2, 0, 0, 0, [
                  { offset: 0, color: 'white' },
                  { offset: 1, color: color }
                ])
                : new graphic.LinearGradient(0, 2, 0, 0, [
                  { offset: 0, color: 'white' },
                  { offset: 1, color: color }
                ]),
              borderRadius: horiz ? [0, 2, 2, 0] : [2, 2, 0, 0]
            }
          }
        }
        return value
      })
    },
    options () {
      return {
        grid: this.horizontal
          ? { top: 20, left: '3%', right: '20%', bottom: '3%', containLabel: true }
          : { top: 40, left: 50, right: 20, bottom: 0, containLabel: true },
        toolbox: {
          show: this.export,
          left: 0,
          feature: {
            dataView: {
              readOnly: true,
              title: this.$t('components.BarChart.dataView'),
              lang: [this.$t('components.BarChart.dataView'), this.$t('components.BarChart.close'), this.$t('components.BarChart.refresh')]
            },
            saveAsImage: {
              type: 'png',
              name: this.exportImageName || this.title || 'ExportedImage',
              title: this.$t('components.BarChart.saveAsImage')
            }
          },
          showTitle: false,
          tooltip: {
            show: true,
            formatter: function (param) {
              return param.title
            }
          }
        },
        tooltip: {
          show: !this.showLabels,
          trigger: 'axis',
          formatter: this.formatTooltip
        },
        xAxis: this.horizontal
          ? {
            type: 'value',
            show: false,
            splitLine: { show: false }
          }
          : {
            type: 'category',
            splitLine: { show: false },
            axisLabel: {
              formatter: this.xAxisTypeIsDate ? this.formatDate : null,
              rotate: 75
            },
            data: this.data.x
          },
        yAxis: this.horizontal
          ? {
            type: 'category',
            axisLine: { show: false },
            axisTick: { show: false },
            data: this.data.x
          }
          : {
            type: 'value',
            name: this.yName,
            nameLocation: 'middle',
            nameGap: this.yAxisNameGap,
            splitLine: { show: false },
            axisLabel: { formatter: this.formatValue }
          },
        series: {
          name: '',
          type: 'bar',
          label: {
            show: this.showLabels,
            position: this.horizontal ? 'right' : 'top',
            formatter: this.formatEchartsValue
          },
          itemStyle: {
            color: this.horizontal
              ? new graphic.LinearGradient(2, 0, 0, 0, [
                { offset: 0, color: 'white' },
                { offset: 1, color: this.color }
              ])
              : new graphic.LinearGradient(0, 2, 0, 0, [
                { offset: 0, color: 'white' },
                { offset: 1, color: this.color }
              ]),
            borderRadius: this.horizontal ? [0, 2, 2, 0] : [2, 2, 0, 0]
          },
          data: this.seriesData
        }
      }
    }
  },
  methods: {
    formatTooltip: function (params) {
      var tooltip = `<b>${this.xAxisTypeIsDate ? this.formatDate(params[0].name) : params[0].name}</b><br>`
      for (let index = params.length - 1; index >= 0; index--) {
        const param = params[index]
        tooltip += `
          <span style="color: lightblue; font-size: 16px;">${this.formatLabelValue(param.value)}${this.sumOneHundred ? '%' : ''}</span>
          <br>
        `
      }
      return tooltip
    },
    formatValue: function (value) {
      if (this.fullPrecisionLabels) {
        return value == null ? '' : String(value)
      }
      return numbro(value).format({
        average: true,
        lowPrecision: false,
        mantissa: this.mantissa,
        optionalMantissa: true
      })
    },
    formatLabelValue: function (value) {
      if (this.fullPrecisionLabels) {
        return value == null ? '' : String(value)
      }
      return this.formatValue(value)
    },
    formatDate (date) {
      if (this.showQuarters) {
        const quarter = moment(date).quarter()
        const year = moment(date).format('YY')
        return `Q${quarter} '${year}`
      } else if (this.period === 'month') {
        return moment(date).format('MMM \'YY')
      } else if (this.period === 'year') {
        return moment(date).format('YYYY')
      } else {
        return moment(date).format('\'YY-MMM-DD')
      }
    },
    formatEchartsValue: function (obj) {
      return `${this.formatLabelValue(obj.value)}`
    }
  },
  props: {
    data: {
      type: Object,
      required: true
    },
    title: {
      type: String,
      required: true
    },
    yName: {
      type: String,
      required: false
    },
    exportImageName: {
      type: String,
      required: false
    },
    icon: {
      type: String,
      required: false,
      default: function () {
        return ''
      }
    },
    precision: {
      type: Number,
      required: false
    },
    xAxisTypeIsDate: {
      type: Boolean,
      required: false
    },
    xAxisRotate: {
      type: Number,
      required: false
    },
    gridBottomMargin: {
      type: Number,
      required: false
    },
    period: {
      type: String,
      required: false,
      default: function () {
        return 'month'
      }
    },
    showLabels: {
      type: Boolean,
      required: false,
      default: function () {
        return true
      }
    },
    color: {
      type: String,
      required: false,
      default: function () {
        return '#6950a1'
      }
    },
    export: {
      type: Boolean,
      required: false,
      default: function () {
        return false
      }
    },
    // Drops the header TextBox would otherwise draw, for a caller that titles the chart itself —
    // the same prop MediaSpendAndReturn and SalesDecomposition carry, and for the same reason.
    hideTitle: {
      type: Boolean,
      required: false,
      default: function () {
        return false
      }
    },
    showQuarters: {
      type: Boolean,
      required: false,
      default: function () {
        return false
      }
    },
    mantissa: {
      type: Number,
      required: false,
      default: 1
    },
    fullPrecisionLabels: {
      type: Boolean,
      required: false,
      default: false
    },
    colors: {
      type: Object,
      required: false,
      default: null
    },
    horizontal: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  components: {
    'v-chart': ECharts,
    TextBox,
    PercentChip
  },
  beforeDestroy () {
    // ECharts instances are automatically cleaned up by vue-echarts
    // but we explicitly clear data references to help garbage collection
    if (this.$refs && this.$refs.chart) {
      this.$refs.chart = null
    }
  }
}
</script>

<style scoped>
.echart-wrap {
  display: block;
  width: 100%;
  height: 355px;
  overflow-y: auto;
}
</style>
